/*!
 * Paradigm Framework - Web Shared
 * Copyright (c) 2017 Miracle Devs, Inc
 * Licensed under MIT (https://github.com/MiracleDevs/Paradigm.Web.Shared/blob/master/LICENSE)
 */

import { Injectable } from '@angular/core';

@Injectable({
    providedIn: 'root'
})
export class ObjectExtensionsService {
    isNull(objectInstance: unknown): boolean {
        return objectInstance === null || objectInstance === undefined;
    }

    getTypeName(objectInstance: unknown): string {
        if (objectInstance === null || objectInstance === undefined) throw new Error('Object can not be null.');

        if (objectInstance.constructor != undefined) {
            const funcNameRegex = /function ([\w]+)\(/;
            const results = funcNameRegex.exec(objectInstance.constructor.toString());
            return results && results.length > 1 ? results[1] : '';
        } else {
            const objType = typeof objectInstance;
            const casedObjectType = objType.charAt(0).toUpperCase() + objType.slice(1);
            return casedObjectType;
        }
    }

    isNativeValueType(objectInstance: unknown): boolean {
        if (objectInstance === null || objectInstance === undefined) throw new Error('Object can not be null.');

        const name = this.getTypeName(objectInstance).toLowerCase();
        return name === 'string' || name === 'number' || name === 'boolean' || name === 'date';
    }

    isEqualTo(
        source: object | undefined,
        other: object | undefined,
        ignore: string[] | undefined = undefined,
        checkObjectType = true,
        checkLength = true
    ): boolean {
        if ((source === null || source === undefined) && (other === null || other === undefined)) return true;

        if ((!source && other) || (source && !other)) return false;

        if (checkLength && source && other && Object.keys(source).length !== Object.keys(other).length) return false;

        if (checkObjectType && this.getTypeName(source) !== this.getTypeName(other)) return false;

        if (
            (typeof source === 'number' && typeof other === 'number') ||
            (typeof source === 'string' && typeof other === 'string') ||
            (typeof source === 'boolean' && typeof other === 'boolean')
        ) {
            return source === other;
        }

        if (source instanceof Date && other instanceof Date) {
            return source.getTime() === other.getTime();
        } else if (typeof other === 'string' && source instanceof Date) {
            const otherDate = new Date(other);
            return source.getTime() === otherDate.getTime();
        } else if (typeof source === 'string' && other instanceof Date) {
            const sourceDate = new Date(source);
            return other.getTime() === sourceDate.getTime();
        }

        if (source instanceof Array && other instanceof Array) {
            if (source.length !== other.length) return false;

            for (let arrayIndex = 0; arrayIndex < source.length; arrayIndex++) {
                if (!this.isEqualTo(source[arrayIndex], other[arrayIndex], ignore, checkObjectType)) {
                    return false;
                }
            }
        } else {
            if (!source) return false;
            const sourceKeys = Object.keys(source);
            for (const key of sourceKeys) {
                if (ignore && ignore.includes(key)) continue;

                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const sourceValue = (source as any)[key];

                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const otherValue = (other as any)[key];

                if (!this.isEqualTo(sourceValue, otherValue, ignore, checkObjectType)) {
                    return false;
                }
            }
        }

        return true;
    }

    getDifference(
        source: object | undefined,
        other: object | undefined,
        ignore: string[] | undefined = undefined,
        checkObjectType = true
    ): string | undefined {
        if (!source && !other) return;

        if ((!source && other) || (source && !other)) return 'different value';

        if (checkObjectType && this.getTypeName(source) !== this.getTypeName(other)) return 'different type';

        if (
            (typeof source === 'number' && typeof other === 'number') ||
            (typeof source === 'string' && typeof other === 'string') ||
            (typeof source === 'boolean' && typeof other === 'boolean')
        ) {
            return source !== other ? 'different value' : undefined;
        }

        if (source instanceof Date && other instanceof Date) {
            return source.getTime() !== other.getTime() ? 'different value' : undefined;
        } else if (typeof other === 'string' && source instanceof Date) {
            const otherDate = new Date(other);
            return source.getTime() !== otherDate.getTime() ? 'different value' : undefined;
        } else if (typeof source === 'string' && other instanceof Date) {
            const sourceDate = new Date(source);
            return other.getTime() !== sourceDate.getTime() ? 'different value' : undefined;
        }

        if (source instanceof Array && other instanceof Array) {
            if (source.length !== other.length) return 'different lengths';

            for (let arrayIndex = 0; arrayIndex < source.length; arrayIndex++) {
                const difference = this.getDifference(source[arrayIndex], other[arrayIndex], ignore, checkObjectType);

                if (difference) {
                    return difference + ' for ' + (arrayIndex + 1) + 'th element';
                }
            }
        } else {
            if (!source) return 'null source';
            const sourceKeys = Object.keys(source);

            for (const key of sourceKeys) {
                if (ignore && ignore.includes(key)) continue;

                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const sourceValue = (source as any)[key];

                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const otherValue = (other as any)[key];

                const difference = this.getDifference(sourceValue, otherValue, ignore, checkObjectType);

                if (difference) {
                    return difference + ' in ' + key;
                }
            }
        }

        return undefined;
    }

    clone(object: object | undefined, ignore?: string[]): object {
        if (typeof object === 'number' || typeof object === 'string' || typeof object === 'boolean') {
            return object;
        }

        if (object instanceof Date) {
            return new Date(object.getTime());
        }

        const newObject = Array.isArray(object) ? [] : {};

        function clone(source: object | undefined, destination: object): void {
            if (!source) return undefined;
            const isArray = source instanceof Array;
            const sourceKeys = isArray ? source : Object.keys(source);

            for (const index in sourceKeys) {
                if (Object.prototype.hasOwnProperty.call(sourceKeys, index)) {
                    const key = isArray ? index : sourceKeys[index];

                    if (!isArray && !Object.prototype.hasOwnProperty.call(source, key)) continue;
                    if (!isArray && ignore && ignore.includes(key)) continue;

                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const thisValue = (source as any)[key];

                    if (
                        typeof thisValue === 'number' ||
                        typeof thisValue === 'string' ||
                        typeof thisValue === 'boolean' ||
                        !thisValue
                    ) {
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        (destination as any)[key] = thisValue;
                    } else if (thisValue instanceof Date) {
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        (destination as any)[key] = new Date(thisValue.valueOf());
                    } else if (thisValue instanceof Array) {
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        (destination as any)[key] = [];
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        clone(thisValue, (destination as any)[key]);
                    } else {
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        (destination as any)[key] = new Object();
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        clone(thisValue, (destination as any)[key]);
                    }
                }
            }
        }

        clone(object, newObject);

        return newObject;
    }

    pickProperties<T extends Record<string | number | symbol, any>>(
        obj: T | undefined,
        keys: (keyof T | string)[]
    ): Partial<T> {
        if (!obj) {
            return {};
        }

        return keys.reduce((result, key) => {
            if (key in obj) {
                (result as T)[key] = obj[key];
            }
            return result;
        }, {} as Partial<T>);
    }
}
